/**
 * Reading a scenario as it ran, versus as it is now.
 *
 * Scenarios stay editable after a suite has been run, so anything displayed
 * alongside a run's transcript or verdict has to come from that run's snapshot
 * — never the live definition. Otherwise a later edit silently rewrites
 * history: the old verdict sits under a script that never produced it.
 */
import type {
  Scenario,
  ScenarioAssertion,
  ScenarioCaller,
  ScenarioCustomer,
  ScenarioMode,
  ScenarioPersona,
  ScenarioResult,
} from './batch-tests';

/**
 * A scenario definition — either the live one or the copy a run snapshotted.
 * One shape for both so no call site can accidentally read the live definition
 * while displaying an old run.
 */
export interface ScenarioDefinition {
  name: string | null;
  question: string;
  followUps: string[];
  assertions: ScenarioAssertion[];
  mode: ScenarioMode;
  persona: ScenarioPersona | null;
  maxTurns: number;
  expectation: string | null;
  customer: ScenarioCustomer | null;
  caller: ScenarioCaller | null;
}

/** The definition as it ran, lifted off a run's snapshot columns. */
export function snapshotOf(result: ScenarioResult): ScenarioDefinition {
  return {
    name: result.scenarioName,
    question: result.scenarioQuestion,
    followUps: result.scenarioFollowUps ?? [],
    assertions: result.scenarioAssertions ?? [],
    mode: result.scenarioMode,
    persona: result.scenarioPersona,
    maxTurns: result.scenarioMaxTurns,
    expectation: result.scenarioExpectation,
    customer: result.scenarioCustomer,
    caller: result.scenarioCaller,
  };
}

/** The live definition, in the same shape. */
export function liveDefinition(scenario: Scenario): ScenarioDefinition {
  return {
    name: scenario.name,
    question: scenario.question,
    followUps: scenario.followUps,
    assertions: scenario.assertions,
    mode: scenario.mode,
    persona: scenario.persona,
    maxTurns: scenario.maxTurns,
    expectation: scenario.expectation,
    customer: scenario.customer,
    caller: scenario.caller,
  };
}

/**
 * Has the scenario been edited since this run?
 *
 * Compares the WHOLE definition, name included. A rename alone changes nothing
 * about what executed, but it still relabels the run in the UI — that is
 * history drift just the same, and it has to be badged.
 *
 * The exception is the simulation-only fields. Switching a scenario to
 * SIMULATED, editing the persona, and switching back leaves that persona on the
 * row: comparing it unconditionally would badge an old SCRIPTED run as drifted
 * over something that never took part in it — and because the read-only view
 * hides persona details in scripted mode, the operator could not even see what
 * supposedly changed. A badge nobody can explain is worse than no badge.
 */
export function divergedFromLive(asRun: ScenarioDefinition, live: ScenarioDefinition): boolean {
  const alwaysMatters =
    (asRun.name ?? '') !== (live.name ?? '') ||
    asRun.question !== live.question ||
    JSON.stringify(asRun.followUps) !== JSON.stringify(live.followUps) ||
    JSON.stringify(asRun.assertions) !== JSON.stringify(live.assertions) ||
    asRun.mode !== live.mode ||
    // The expectation is NOT simulation-only: the judge grades a scripted run
    // against it too.
    (asRun.expectation ?? '') !== (live.expectation ?? '') ||
    // Nor is the seeded caller. It decides whether Iris was talking to a
    // stranger or to a member, which changes what the transcript even means —
    // adding a membership to a scenario makes every earlier run a test of
    // something else.
    JSON.stringify(asRun.customer ?? null) !== JSON.stringify(live.customer ?? null) ||
    // Nor is the arriving identity, for the same reason and more strongly: it
    // decides WHICH real customer Iris resolved, so repointing it makes every
    // earlier run a test of a different person.
    JSON.stringify(asRun.caller ?? null) !== JSON.stringify(live.caller ?? null);
  if (alwaysMatters) return true;

  // Persona and turn budget shaped the conversation only if simulation was
  // actually in play — on either side, so switching INTO simulated still counts
  // (that difference is already caught by the mode check above).
  const simulationInPlay = asRun.mode === 'SIMULATED' || live.mode === 'SIMULATED';
  if (!simulationInPlay) return false;
  return (
    JSON.stringify(asRun.persona) !== JSON.stringify(live.persona) ||
    asRun.maxTurns !== live.maxTurns
  );
}
